import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useToastStore } from './toast-store';
import type { AppToast, AppToastOptions } from './types';

/**
 * Public API. Identical signature to the previous `@tamagui/toast`-backed
 * version — consumers (`useShowSuggestionToast`, etc.) didn't need to change
 * when we swapped the underlying implementation.
 */
export function useAppToast(): AppToast {
  return useMemo<AppToast>(() => {
    const show = (title: string, opts?: AppToastOptions | undefined): void => {
      useToastStore.getState().show(title, opts);
    };

    return {
      show,
      info: (title, opts) => show(title, { ...opts, variant: 'info' }),
      success: (title, opts) => show(title, { ...opts, variant: 'success' }),
      warning: (title, opts) => show(title, { ...opts, variant: 'warning' }),
      error: (title, opts) => show(title, { ...opts, variant: 'error' }),
      custom: (content: ReactNode, opts?: Omit<AppToastOptions, 'content'> | undefined) =>
        show('', { ...opts, content }),
      hide: () => useToastStore.getState().hide(),
    };
  }, []);
}
